import { Form } from "@circle-react-uikit/Form";
import { noop } from "lodash";
import type { ReactNode } from "react";
import { useCallback, useEffect, useRef, useState } from "react";

import type { CreateSubmissionPayload } from "@/react/api/submissionsApi";
import { TurnstileWrapper } from "@/react/components/OnboardingPage/CreateCommunityPage/TurnstileWrapper";
import { useTurnstile } from "@/react/components/OnboardingPage/hooks/useTurnstile";
import { usePunditUserContext } from "@/react/contexts/punditUserContext";
import { classNames } from "@/react/helpers/twMergeWithCN";

import { useFormRenderingContext } from "../contexts/FormRenderingContext";
import { useSubmitForm } from "../hooks/useSubmitForm";
import { FormWrapperCompass } from "./FormWrapperCompass";

interface FormWrapperProps {
  children: ReactNode;
}

export const FormWrapper = ({ children }: FormWrapperProps) => {
  const { isLoading } = usePunditUserContext();
  const {
    form,
    isPreview,
    usage,
    formStyles,
    formDomId,
    formBackgroundColor,
    shouldRenderEndingPage,
    isModal,
    setFormHeight,
    setIsSubmitting,
    index,
  } = useFormRenderingContext();

  const {
    turnstileRef,
    dispatch,
    resetTurnstileToken,
    shouldShowTurnstile,
    shouldUseTurnstile,
    turnstileSiteKey,
    turnstileResult,
  } = useTurnstile(isPreview);

  const formRef = useRef<HTMLDivElement>(null);
  const [isFormConnected, setIsFormConnected] = useState(false);
  const { mutate } = useSubmitForm({ onError: resetTurnstileToken });
  const pendingSubmissionRef = useRef<CreateSubmissionPayload | null>(null);

  const submitForm = useCallback(
    (payload: CreateSubmissionPayload, turnstileToken?: string) => {
      const transformedPayload = {
        ...payload,
        turnstile_token: turnstileToken,
        elements: payload.elements.map(element => {
          if (Array.isArray(element.value)) {
            return {
              ...element,
              value_json: element.value,
            };
          }
          return element;
        }),
      };
      mutate(transformedPayload);
    },
    [mutate],
  );

  useEffect(() => {
    const turnstileToken = turnstileRef.current?.getResponse();
    if (!turnstileToken) return;

    const pendingSubmission = pendingSubmissionRef.current;
    if (!pendingSubmission) return;

    pendingSubmissionRef.current = null;
    submitForm(pendingSubmission, turnstileToken);
  }, [submitForm, turnstileRef, turnstileResult]);

  useEffect(() => {
    const formElement = formRef.current;
    if (!shouldUseTurnstile || !formElement) return;

    if (formElement.isConnected) {
      setIsFormConnected(true);
      return;
    }

    const observer = new MutationObserver(() => {
      if (!formElement.isConnected) return;

      setIsFormConnected(true);
      observer.disconnect();
    });
    observer.observe(document.documentElement, {
      childList: true,
      subtree: true,
    });

    return () => observer.disconnect();
  }, [form.uid, shouldUseTurnstile]);

  const handleSubmit = (payload: CreateSubmissionPayload) => {
    const turnstileToken = turnstileRef.current?.getResponse();
    if (shouldUseTurnstile && !turnstileToken) {
      pendingSubmissionRef.current = payload;
      setIsSubmitting(true);
      return;
    }

    submitForm(payload, turnstileToken);
  };

  useEffect(() => {
    if (!isLoading) {
      setFormHeight(formRef.current?.scrollHeight || 0);
    }
  }, [isLoading, setFormHeight]);

  return (
    <FormWrapperCompass>
      <div ref={formRef} id={`form-wrapper-${form.uid}-${index}`}>
        {!isLoading && (
          <Form
            id={formDomId}
            onSubmit={isPreview ? noop : handleSubmit}
            className={classNames("w-full", {
              "max-w-[600px]": !isModal && !usage,
            })}
            formClassName="mb-0"
            style={{
              backgroundColor: !usage ? formBackgroundColor : undefined,
            }}
          >
            <style>{formStyles}</style>
            {children}
            {shouldUseTurnstile && isFormConnected && (
              <div
                className={classNames("mt-6 flex w-full justify-center", {
                  hidden: !shouldShowTurnstile || shouldRenderEndingPage,
                })}
              >
                <TurnstileWrapper
                  turnstileRef={turnstileRef}
                  dispatch={dispatch}
                  shouldShowTurnstile={shouldShowTurnstile}
                  siteKey={turnstileSiteKey}
                />
              </div>
            )}
          </Form>
        )}
      </div>
    </FormWrapperCompass>
  );
};
